import { trackEvent } from 'user-analytics';

/**
 * Serializes product data for Mixpanel tracking.
 * Maps frontend camelCase properties to mobile-compatible tracking format.
 *
 * @param {Object} product - Product object from catalog/cart state.
 * @returns {Object} Serialized product properties for tracking.
 */
function serializeProduct(product) {
  return {
    'Product Code': product.code || '',
    'Product Name': product.name || '',
    'Product Unit Price': product.price || 0,
    'Stock Available': product.stock || 0,
  };
}

/**
 * Serializes purchase context data for product tracking events.
 * Extracts token, terminal ID, departure origin and destination from purchase state.
 * Terminal ID is derived from the departure origin terminal (fragments[0].origin.id).
 *
 * @param {Object} purchase - Purchase object from Redux state (plain JS).
 * @returns {Object} Serialized purchase context properties for tracking.
 */
function serializePurchaseContext(purchase) {
  const firstFragment = purchase.departs?.fragments?.[0];

  return {
    'Purchase Token': purchase.token || '',
    'Terminal ID': firstFragment?.origin?.id || '',
    'Departure Origin': firstFragment?.origin?.cityName || '',
    'Departure Destination': firstFragment?.destination?.cityName || '',
  };
}

/**
 * Tracks when a product is added to the cart.
 * Event name matches mobile app: "Add Product to Cart".
 *
 * @param {Object} product - Product object with code, name, price, stock.
 * @param {number} quantity - Quantity being added.
 * @param {Object} purchase - Purchase object from Redux state (plain JS).
 */
export function trackAddProductToCart(product, quantity, purchase) {
  const unitPrice = product.price || 0;

  trackEvent('Add Product to Cart', {
    ...serializeProduct(product),
    'Quantity': quantity,
    'Total Price': unitPrice * quantity,
    ...serializePurchaseContext(purchase),
  });
}

/**
 * Tracks when a product is removed from the cart.
 * Event name matches mobile app: "Remove Product from Cart".
 *
 * @param {Object} product - Product object with code, name, price, quantity.
 * @param {Object} purchase - Purchase object from Redux state (plain JS).
 */
export function trackRemoveProductFromCart(product, purchase) {
  trackEvent('Remove Product from Cart', {
    'Product Code': product.code || '',
    'Product Name': product.name || '',
    'Product Unit Price': product.price || 0,
    'Quantity': product.quantity || 0,
    'Purchase Token': purchase.token || '',
  });
}

/**
 * Tracks when the product catalog is viewed/loaded successfully.
 * Event name matches mobile app: "Product Catalog Viewed".
 *
 * @param {string} terminalId - Terminal ID associated with the catalog.
 * @param {number} catalogSize - Total number of products in the catalog.
 * @param {string} departureDate - Departure date string.
 */
export function trackProductCatalogViewed(terminalId, catalogSize, departureDate) {
  trackEvent('Product Catalog Viewed', {
    'Terminal ID': terminalId || '',
    'Product Count': catalogSize || 0,
    'Departure Date': departureDate || '',
    'Catalog Available': catalogSize > 0,
  });
}
